/**
 * Wer gerade angemeldet ist — im Demonstrationsstand zum Umschalten.
 *
 * **Warum es das gibt.** Die Shell hat bis hierhin mit einer leeren Rollenliste
 * gearbeitet, und der Kommentar dort nennt den Grund: Wer zum Entwickeln
 * `tenant-admin` einträgt, hat den Administrationspunkt für alle sichtbar
 * gemacht, und auffallen würde es erst, wenn die Anmeldung steht.
 *
 * Das Argument bleibt richtig, aber es hat eine Kehrseite: Mit einer leeren
 * Liste ist die Administration **nie** erreichbar, und die Sicht des externen
 * Partners auch nicht. Zwei der sechs Rollen aus `07` §7.2 sind damit nicht
 * beurteilbar — ausgerechnet die beiden, bei denen ein Fehler etwas kostet.
 *
 * Die Auflösung ist nicht, eine Rolle anzunehmen, sondern sie **sichtbar
 * wählbar** zu machen. Wer im Portal steht, sieht in der Kopfzeile, als wen er
 * es tut. Eine angenommene Rolle ist unsichtbar und deshalb gefährlich; eine
 * gewählte steht im Bild.
 *
 * **Das ist keine Authentifizierung.** Es setzt einen Zustand im Browser und
 * prüft nichts. `IAM-001` hält den Vertrag bereit, Keycloak macht die Sache
 * später wirklich. Bis dahin gilt, was `01` §1.11 ohnehin verlangt: Die
 * Berechtigung entscheidet das Quellsystem, nicht das Portal — ein verstecktes
 * Menü hat noch nie etwas geschützt.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import type { Rolle } from "@hwc/contracts";
import { formatResourceRef } from "@hwc/contracts";
import type { Auditeintrag, Bestand, Person, Servicebericht } from "@hwc/demo-data";
import { erzeugeBestand, holeStand, leereStand, meldeStand } from "@hwc/demo-data";

import { KEINE_VORGAENGE, ueberlagere } from "./vorgaenge.js";
import type { Ausgang, Vorgaenge } from "./vorgaenge.js";

export interface Sitzung {
  /** Der vollständige Demobestand, einmal erzeugt. */
  readonly bestand: Bestand;
  /** Wer gerade angemeldet ist. */
  readonly person: Person;
  readonly rollen: readonly Rolle[];
  /** Der Bezugstag, auf den der Bestand gerechnet wurde. */
  readonly heute: Date;
  wechsleZu(person: Person): void;

  /** Was in dieser Sitzung entschieden wurde — roh, ohne Überlagerung. */
  readonly vorgaenge: Vorgaenge;
  /**
   * Führt eine Änderung aus.
   *
   * Nimmt die Funktion, nicht das Ergebnis: So bekommt sie den **aktuellen**
   * Stand der Vorgänge, auch wenn seit dem Rendern schon etwas passiert ist.
   * Gibt die Absage zurück, wenn eine Regel greift — die Oberfläche zeigt sie
   * an, statt stillschweigend nichts zu tun.
   */
  fuehreAus(schritt: (v: Vorgaenge) => Ausgang): string | undefined;
  /** Setzt alles zurück, was diese Sitzung geändert hat. */
  verwirfVorgaenge(): void;
}

const SitzungsKontext = createContext<Sitzung | undefined>(undefined);

/**
 * Wen wir uns zuletzt gemerkt haben.
 *
 * In `try`/`catch`, weil `localStorage` nicht überall da ist und nicht überall
 * antwortet: In einem privaten Fenster, bei blockierten Website-Daten oder in
 * einer Vorschau wirft schon der Zugriff. Ein Portal, das deshalb mit einer
 * weißen Seite endet, wäre ein Portal, das an einer Bequemlichkeit scheitert —
 * dieselbe Überlegung steht in `erscheinung.ts`.
 */
const SCHLUESSEL = "hwc.person";

function lies(): string | undefined {
  try {
    return window.localStorage.getItem(SCHLUESSEL) ?? undefined;
  } catch {
    return undefined;
  }
}

function merke(person: Person): void {
  try {
    window.localStorage.setItem(SCHLUESSEL, person.ref.opaqueId);
  } catch {
    // Nicht merken können ist kein Fehler, den jemand sehen muss.
  }
}

/**
 * Die Vorgänge dieser Sitzung, gemerkt.
 *
 * **Warum überhaupt.** Wer den Ablauf Monteur → Büro prüfen will, hat zwei
 * Fenster offen und lädt zwischendurch neu. Die Outbox der Monteur-App
 * übersteht das seit dem ersten Entwurf; das Portal warf seine Entscheidungen
 * weg, und damit war die interessanteste Prüfung — der Bericht kommt an, das
 * Büro gibt ihn frei — nach jedem Neuladen wieder am Anfang.
 *
 * **Im Betrieb gibt es das nicht.** Dort liegen diese Entscheidungen in den
 * Fachdiensten und kommen als Projektion zurück (ADR-021); der Browser merkt
 * sich nichts. Was hier steht, ersetzt den Server, den es noch nicht gibt —
 * und es ersetzt ihn schlecht, denn zwei Browser sehen verschiedene Stände.
 * Für einen Probierstand auf einem Rechner genügt es.
 */
const VORGANGSSCHLUESSEL = "hwc.portal.vorgaenge";

function liesVorgaenge(): Vorgaenge {
  try {
    const roh = window.localStorage.getItem(VORGANGSSCHLUESSEL);
    if (!roh) return KEINE_VORGAENGE;
    const gelesen = JSON.parse(roh) as Partial<Vorgaenge>;
    // Feld für Feld statt eines Sturzes in den Typ: Ein Stand aus einer
    // früheren Fassung darf nicht dazu führen, dass das Portal beim Start
    // über ein fehlendes Feld stolpert.
    return {
      einsatzzustand: gelesen.einsatzzustand ?? {},
      eingang: gelesen.eingang ?? {},
      bericht: gelesen.bericht ?? {},
      neueAuftraege: gelesen.neueAuftraege ?? [],
      neueBerichte: gelesen.neueBerichte ?? [],
      wartung: gelesen.wartung ?? {},
      neueLinks: gelesen.neueLinks ?? [],
      neueBeitraege: gelesen.neueBeitraege ?? [],
      neueKunden: gelesen.neueKunden ?? [],
      zurueckgeholt: gelesen.zurueckgeholt ?? [],
      entfernt: gelesen.entfernt ?? [],
      widerrufen: gelesen.widerrufen ?? [],
      audit: gelesen.audit ?? [],
    };
  } catch {
    return KEINE_VORGAENGE;
  }
}

function merkeVorgaenge(vorgaenge: Vorgaenge): void {
  try {
    window.localStorage.setItem(VORGANGSSCHLUESSEL, JSON.stringify(vorgaenge));
  } catch {
    // s. `merke` oben.
  }
}

function vergissVorgaenge(): void {
  try {
    window.localStorage.removeItem(VORGANGSSCHLUESSEL);
  } catch {
    // s. o.
  }
}

/**
 * Der Bestand wird **einmal je Sitzung** erzeugt und nicht bei jedem Render.
 *
 * Sonst bekäme jede Ansicht frische Objekte, und alles, was auf Referenzgleichheit
 * baut — Memoisierung, Listenschlüssel, Vergleiche — arbeitete ins Leere.
 * Zusätzlich wandert `new Date()` sonst zwischen zwei Rendern, und ein Einsatz
 * könnte während des Blätterns aus „heute" herausfallen.
 */
export function SitzungsRahmen({ children }: { children: ReactNode }) {
  const [heute] = useState(() => new Date());
  const [urbestand] = useState(() => erzeugeBestand(heute));
  const [vorgaenge, setzeVorgaenge] = useState<Vorgaenge>(liesVorgaenge);

  /**
   * Der Bestand, den alle Ansichten sehen: die Projektion plus das, was das
   * Büro in dieser Sitzung entschieden hat.
   *
   * **Deshalb muss keine Ansicht von den Vorgängen wissen.** Sie lesen weiter
   * `bestand.einsaetze`. Die Alternative — jede Ansicht fragt zusätzlich die
   * Vorgänge ab — hat genau eine Stelle, die man vergisst, und dann zeigt eine
   * Liste einen anderen Zustand als die Detailansicht daneben.
   */
  const bestand = useMemo(
    () => ueberlagere(urbestand, vorgaenge),
    [urbestand, vorgaenge],
  );

  // Vorbelegt mit der Bürokraft: Sie hat nach `07` §7.2 die breiteste
  // Arbeitssicht — Eingang, Kundenakte, Auftrag anlegen, Zuweisung, Abschluss
  // prüfen. Wer das Portal zum ersten Mal öffnet, soll etwas zu tun finden.
  //
  // **Die Wahl überlebt das Neuladen**, aus demselben Grund wie die
  // Darstellungswahl in `erscheinung.ts`: Wer die Sicht des Monteurs mit der
  // des Büros vergleicht, lädt zwischendurch neu — und findet sich sonst
  // jedes Mal wieder als Bürokraft, ohne dass es ihm auffällt. Genau der
  // Vergleich ist der Zweck dieses Umschalters.
  const [person, setzePerson] = useState<Person>(() => {
    const gemerkt = lies();
    const treffer = gemerkt
      ? urbestand.personen.find((p) => p.ref.opaqueId === gemerkt)
      : undefined;
    // Ein gemerkter Wert, den es nicht mehr gibt, wird stillschweigend
    // verworfen statt zu einer leeren Sitzung zu führen — der Bestand kann
    // sich zwischen zwei Ständen geändert haben.
    const buero = urbestand.personen.find((p) => p.kuerzel === "SK");
    return treffer ?? buero ?? urbestand.personen[0]!;
  });

  // Jede Entscheidung landet sofort im Speicher — dieselbe Überlegung wie bei
  // der Outbox der Monteur-App: Wer das Fenster schließt, hat nicht
  // gespeichert, er hat aufgehört zu klicken.
  useEffect(() => {
    merkeVorgaenge(vorgaenge);
  }, [vorgaenge]);

  /**
   * Was das Büro entscheidet, geht an den Austauschdienst.
   *
   * Ohne diesen Weg sieht die Monteur-App nichts davon — sie läuft auf einer
   * anderen Herkunft und hat keinen Zugriff auf diesen Speicher. Eine
   * Rückgabe, die den Einsatz auf `abschluss` zurücksetzt, käme beim Monteur
   * nie an, und er stünde vor einem Bericht, den er nicht erneut senden kann.
   *
   * Fehlschläge werden geschluckt: Der Dienst gehört zum Entwicklungsserver
   * des Portals, und ein Portal, das ohne ihn nicht mehr bedienbar wäre, hätte
   * eine Abhängigkeit, die es im Betrieb nicht gibt.
   */
  useEffect(() => {
    if (vorgaenge.audit.length === 0) return;
    void meldeStand({
      einsatzzustand: Object.fromEntries(
        Object.entries(vorgaenge.einsatzzustand),
      ),
      eingang: vorgaenge.eingang,
      neueAuftraege: vorgaenge.neueAuftraege,
      bericht: {
        ...vorgaenge.bericht,
        // Auch die aus dem Feld, damit ein zweites Fenster sie sieht.
        ...Object.fromEntries(
          vorgaenge.neueBerichte.map((b) => [formatResourceRef(b.ref), b]),
        ),
      },
      widerrufen: vorgaenge.widerrufen,
      audit: vorgaenge.audit,
    });
  }, [vorgaenge]);

  /**
   * Was die Monteur-App gemeldet hat, kommt herein.
   *
   * **Beim Laden und bei jedem Fensterwechsel**, nicht in einer Schleife. Wer
   * den Ablauf über beide Oberflächen prüft, wechselt zwischen zwei Fenstern —
   * genau dann soll das Portal frisch sein. Ein Abfrageintervall wäre die
   * bequemere Antwort und würde einen Dienst belasten, den es im Betrieb so
   * nicht gibt; dort schiebt die Projektion.
   */
  useEffect(() => {
    let abgebrochen = false;

    const abholen = () => {
      void holeStand().then((geteilt) => {
        if (abgebrochen || !geteilt) return;
        setzeVorgaenge((alt) => {
          const bekannt = new Set(alt.audit.map((e) => e.ref.opaqueId));
          const dazu = (geteilt.audit as Auditeintrag[]).filter(
            (e) => e?.ref?.opaqueId && !bekannt.has(e.ref.opaqueId),
          );

          const fremd = geteilt.einsatzzustand as Vorgaenge["einsatzzustand"];
          const neueZustaende = Object.entries(fremd).filter(
            ([ref, wert]) => alt.einsatzzustand[ref] !== wert,
          );

          /**
           * Berichte, die im Feld entstanden sind.
           *
           * Die Monteur-App meldet einen vollständigen Datensatz, keinen
           * Änderungssatz — der Bericht existiert im Bestand nicht. Bekannte
           * werden übersprungen: Das Büro hat sie womöglich schon
           * freigegeben, und eine erneute Meldung darf das nicht zurückdrehen.
           */
          const bekannteBerichte = new Set([
            ...alt.neueBerichte.map((b) => b.ref.opaqueId),
            ...Object.keys(alt.bericht),
          ]);
          const ausDemFeld = Object.values(
            geteilt.bericht as Record<string, Servicebericht>,
          ).filter(
            (b) => b?.ref?.opaqueId && !bekannteBerichte.has(b.ref.opaqueId),
          );

          /**
           * **Nicht am Audit festmachen, ob etwas Neues da ist.**
           *
           * Der erste Entwurf stieg aus, wenn kein unbekannter Auditeintrag
           * dabei war — und die Monteur-App meldet keinen: Sie erzeugt
           * Offlineoperationen, keine Protokolleinträge. Der übermittelte
           * Einsatz kam damit nie im Portal an, und der Ablauf, für den es
           * den ganzen Dienst gibt, war weiter nicht prüfbar.
           *
           * Aufgefallen am 05.09.2026 beim ersten Durchgang über beide
           * Fenster — also genau dort, wo vorher nur eine Anleitung stand.
           */
          if (
            dazu.length === 0
            && neueZustaende.length === 0
            && ausDemFeld.length === 0
          ) {
            return alt;
          }

          return {
            ...alt,
            // Der lokale Stand gewinnt: Was das Büro gerade entschieden hat,
            // ist neuer als das, was der Dienst davon weiß.
            einsatzzustand: { ...fremd, ...alt.einsatzzustand },
            neueBerichte: [...alt.neueBerichte, ...ausDemFeld],
            audit: [...alt.audit, ...dazu],
          };
        });
      });
    };

    abholen();
    window.addEventListener("focus", abholen);
    return () => {
      abgebrochen = true;
      window.removeEventListener("focus", abholen);
    };
  }, []);

  const wechsleZu = useCallback((neu: Person) => {
    setzePerson(neu);
    merke(neu);
  }, []);

  /**
   * Führt einen Schritt aus und gibt die Absage zurück, falls eine kam.
   *
   * Der Rückgabewert ist kein Nebenprodukt: Eine Regel, die greift, ohne dass
   * jemand es sieht, ist von einem kaputten Knopf nicht zu unterscheiden.
   * `05` §5.12 verlangt für auslösende Aktionen eine sichtbare Bestätigung —
   * und für eine Absage gilt dasselbe.
   */
  const fuehreAus = useCallback(
    (schritt: (v: Vorgaenge) => Ausgang): string | undefined => {
      let absage: string | undefined;
      setzeVorgaenge((alt) => {
        const ergebnis = schritt(alt);
        if (ergebnis.ok) return ergebnis.vorgaenge;
        absage = ergebnis.grund;
        return alt;
      });
      return absage;
    },
    [],
  );

  const verwirfVorgaenge = useCallback(() => {
    setzeVorgaenge(KEINE_VORGAENGE);
    vergissVorgaenge();
    // Auch beim Dienst: Sonst holt der nächste Abgleich alles zurück, und das
    // Zurücksetzen sähe aus wie ein Fehler.
    void leereStand();
  }, []);

  const wert = useMemo<Sitzung>(
    () => ({
      bestand,
      person,
      rollen: person.rollen,
      heute,
      wechsleZu,
      vorgaenge,
      fuehreAus,
      verwirfVorgaenge,
    }),
    [bestand, person, heute, wechsleZu, vorgaenge, fuehreAus, verwirfVorgaenge],
  );

  return <SitzungsKontext.Provider value={wert}>{children}</SitzungsKontext.Provider>;
}

/**
 * Die laufende Sitzung.
 *
 * Wirft, wenn kein Rahmen darüber liegt. Ein stillschweigender Rückfall auf
 * einen leeren Bestand wäre die schlechtere Wahl: Die Ansicht wäre dann leer,
 * und niemand wüsste, ob das an den Daten liegt oder daran, dass der Rahmen
 * fehlt.
 */
export function useSitzung(): Sitzung {
  const wert = useContext(SitzungsKontext);
  if (!wert) throw new Error("useSitzung ohne <SitzungsRahmen> aufgerufen");
  return wert;
}

/** Trägt der angemeldete Benutzer mindestens eine dieser Rollen? */
export function useHatRolle(...gefordert: readonly Rolle[]): boolean {
  const { rollen } = useSitzung();
  return gefordert.some((r) => rollen.includes(r));
}

/** Der Anzeigename zu einem Personen-Ref, oder ein ehrliches Ersatzwort. */
export function useName(): (ref: { readonly opaqueId: string } | undefined) => string {
  const { bestand } = useSitzung();
  return useCallback(
    (ref) => {
      if (!ref) return "nicht zugewiesen";
      const treffer = bestand.personen.find((p) => p.ref.opaqueId === ref.opaqueId);
      return treffer?.name ?? "unbekannt";
    },
    [bestand],
  );
}

/** Vergleicht zwei Refs über ihre Zeichenkettenform. */
export function refGleich(
  a: Parameters<typeof formatResourceRef>[0] | undefined,
  b: Parameters<typeof formatResourceRef>[0] | undefined,
): boolean {
  if (!a || !b) return false;
  return formatResourceRef(a) === formatResourceRef(b);
}
